<template>
  <div class="container-fluid py-5">
    <div class="row justify-content-center">
      <div class="col-lg-8">
        <!-- Carte principale -->
        <div class="card shadow-lg border-0">
          <!-- Header succès -->
          <div class="card-header text-center py-4" style="background: linear-gradient(135deg, #28a745 0%, #20c997 100%);">
            <i class="fa fa-check-circle fa-4x text-white mb-3"></i>
            <h2 class="text-white fw-bold mb-0">Dossier soumis avec succès !</h2>
          </div>

          <div class="card-body p-4">
            <!-- Info BESC -->
            <div class="alert alert-info mb-4">
              <h5 class="mb-2">
                <i class="fa fa-file-text me-2"></i>
                Référence BESC : <strong>{{ besc?.refbesc }}</strong>
              </h5>
              <p class="mb-0">
                Statut actuel : 
                <span class="badge bg-warning text-dark">
                  {{ besc?.currentStatus?.label || 'Soumis - En attente de paiement' }}
                </span>
              </p>
            </div>

            <!-- Info Facture -->
            <div class="card bg-light mb-4">
              <div class="card-body">
                <h5 class="mb-3">
                  <i class="fa fa-file-invoice-dollar me-2"></i>
                  Facture générée
                </h5>
                
                <div class="row mb-3">
                  <div class="col-6">
                    <small class="text-muted">Référence facture</small>
                    <p class="fw-bold mb-0">{{ facture?.referenceFacture }}</p>
                  </div>
                  <div class="col-6">
                    <small class="text-muted">Date d'émission</small>
                    <p class="fw-bold mb-0">{{ formatDate(facture?.issueDate) }}</p>
                  </div>
                </div>

                <div class="row mb-3">
                  <div class="col-6">
                    <small class="text-muted">Date d'échéance</small>
                    <p class="fw-bold mb-0">{{ formatDate(facture?.dueDate) }}</p>
                  </div>
                  <div class="col-6">
                    <small class="text-muted">Statut</small>
                    <p class="mb-0">
                      <span class="badge bg-warning text-dark">
                        {{ facture?.status }}
                      </span>
                    </p>
                  </div>
                </div>

                <!-- Montant total -->
                <div class="border-top pt-3 mt-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="mb-0">Montant à payer :</h4>
                    <h3 class="mb-0 text-primary fw-bold">
                      {{ formatMoney(facture?.montantTotal) }} {{ facture?.currencyCode }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="d-grid gap-3">
              <button 
                class="btn btn-primary btn-lg"
                @click="proceedToPayment"
              >
                <i class="fa fa-credit-card me-2"></i>
                Procéder au paiement
              </button>

              <button 
                class="btn btn-outline-secondary"
                @click="downloadInvoice"
              >
                <i class="fa fa-download me-2"></i>
                Télécharger la facture
              </button>

              <router-link 
                :to="{ name: 'ListeBesc' }"
                class="btn btn-outline-primary"
              >
                <i class="fa fa-list me-2"></i>
                Retour à la liste des BESC
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ApiService from '@/services/ApiService'
import { error, success } from '@/utils/utils'

export default defineComponent({
  name: 'PageSoumissionBesc',
  
  setup() {
    const route = useRoute()
    const router = useRouter()
    const bescId = route.params.id as string

    const besc = ref<any>(null)
    const facture = ref<any>(null)
    const loading = ref(false)

    const loadData = async () => {
      // Essayer de récupérer depuis le state de navigation
      const submissionData = history.state?.submissionData

      if (submissionData) {
        besc.value = submissionData.besc
        facture.value = submissionData.facture
        return
      }

      // Sinon, recharger depuis l'API
      loading.value = true
      try {
        const [bescRes, factureRes] = await Promise.all([
          ApiService.get(`/besc/${bescId}`),
          ApiService.get(`/factures?bescId=${bescId}`)
        ])

        besc.value = bescRes.data.data
        facture.value = factureRes.data.data.data[0] // Dernière facture
      } catch (err: any) {
        error('Erreur lors du chargement des données')
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    const formatDate = (date: string | Date) => {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const formatMoney = (amount: number) => {
      if (!amount) return '0'
      return amount.toLocaleString('fr-FR')
    }

    const proceedToPayment = () => {
      router.push({
        name: 'BescPayment',
        params: { id: bescId },
        query: { factureId: facture.value?.id }
      })
    }

    const downloadInvoice = async () => {
      try {
        const response = await ApiService.getFile(
          `/factures/${facture.value.id}/download`
        )
        
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Facture_${facture.value.referenceFacture}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        
        success('Facture téléchargée avec succès')
      } catch (err: any) {
        error('Erreur lors du téléchargement')
      }
    }

    onMounted(() => {
      loadData()
    })

    return {
      besc,
      facture,
      loading,
      formatDate,
      formatMoney,
      proceedToPayment,
      downloadInvoice
    }
  }
})
</script>

<style scoped>
.card {
  border-radius: 12px;
}

.card-header {
  border-radius: 12px 12px 0 0 !important;
}

.btn-lg {
  padding: 12px 24px;
  font-size: 1.1rem;
}

.d-grid {
  display: grid;
}
</style>